import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from '@phosphor-icons/react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { OptionRowsSkeleton, PageHeader } from '@/components/core';
import { Input } from '@/components/ui-kit/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useServerTable } from '@/hooks/use-server-table';
import { MbmProjectFormDialog } from '../../components/mbm-project-form-dialog/mbm-project-form-dialog';
import { MbmProjectHistoryDialog } from '../../components/mbm-project-history-dialog/mbm-project-history-dialog';
import { DEFAULT_MBM_PROJECTS_ORDER } from '../../graphql/queries';
import { createMbmProjectTableColumns } from '../../components/mbm-project-table-columns/mbm-project-table-columns';
import {
  useGetMbmCustomers,
  useGetMbmProjects,
  useUpdateMbmProject,
} from '../../hooks/use-mbm-projects';
import {
  MBM_ACTIVE_STATUS,
  MBM_INACTIVE_STATUS,
  MbmCustomer,
  MbmProject,
  MbmProjectsPagingResult,
  MbmProjectStatusFilter,
} from '../../types/mbm-project.types';

const ALL_CUSTOMERS = 'all';

export const MbmProjectManagerPage = () => {
  const { t } = useTranslation();
  const canCreate = useHasPermission(HRM_PERMISSIONS.MBM_PROJECT.CREATE);
  const canUpdate = useHasPermission(HRM_PERMISSIONS.MBM_PROJECT.UPDATE);

  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const [customerId, setCustomerId] = useState(ALL_CUSTOMERS);
  const [status, setStatus] = useState<MbmProjectStatusFilter>('all');
  const { pagination, resetPage, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_MBM_PROJECTS_ORDER,
  });

  const [formState, setFormState] = useState<{ open: boolean; project: MbmProject | null }>({
    open: false,
    project: null,
  });
  const [historyState, setHistoryState] = useState<{ open: boolean; project: MbmProject | null }>({
    open: false,
    project: null,
  });
  const [statusState, setStatusState] = useState<{ open: boolean; project: MbmProject | null }>({
    open: false,
    project: null,
  });

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedKeyword(keyword.trim()), 600);
    return () => clearTimeout(timeout);
  }, [keyword]);

  useEffect(() => {
    resetPage();
  }, [debouncedKeyword, customerId, status, resetPage]);

  const { data: customersData, isLoading: isCustomersLoading } = useGetMbmCustomers();
  const customers = (customersData as MbmCustomer[] | undefined) ?? [];
  const { data, isLoading, error } = useGetMbmProjects({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    search: debouncedKeyword,
    customerId: customerId === ALL_CUSTOMERS ? undefined : customerId,
    status,
    filter: columnWhere,
    order,
  });
  const result = (data as { MbmProjects?: MbmProjectsPagingResult })?.MbmProjects;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const { mutateAsync: updateProject, isPending: isUpdatingStatus } = useUpdateMbmProject();

  const projectToToggle = statusState.project;
  const willActivate = projectToToggle?.Status !== MBM_ACTIVE_STATUS;

  const handleStatusConfirm = async () => {
    if (!projectToToggle) return;
    await updateProject({
      project: projectToToggle,
      values: {
        ProjectId: projectToToggle.ProjectId,
        ProjectName: projectToToggle.ProjectName,
        CustomerId: projectToToggle.CustomerId,
        CustomerName: projectToToggle.CustomerName,
        Status: willActivate ? MBM_ACTIVE_STATUS : MBM_INACTIVE_STATUS,
      },
    });
    setStatusState({ open: false, project: null });
  };

  const columns = createMbmProjectTableColumns({
    t,
    onEdit: (project) => setFormState({ open: true, project }),
    onToggleStatus: (project) => setStatusState({ open: true, project }),
    onViewHistory: (project) => setHistoryState({ open: true, project }),
    canUpdate,
  });

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        title={t('MBM_PROJECT_MANAGER')}
        actions={
          canCreate
            ? [
                {
                  label: t('MBM_PROJECT_CREATE'),
                  icon: <Plus className="size-5" />,
                  onClick: () => setFormState({ open: true, project: null }),
                },
              ]
            : []
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Input
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          placeholder={t('MBM_PROJECT_SEARCH_PLACEHOLDER')}
          aria-label={t('SEARCH')}
          className="w-full sm:w-72"
        />
        <Select value={customerId} onValueChange={setCustomerId}>
          <SelectTrigger className="w-full sm:w-56" aria-label={t('CUSTOMER')}>
            <SelectValue placeholder={t('CUSTOMER')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_CUSTOMERS}>{t('MBM_PROJECT_ALL_CUSTOMERS')}</SelectItem>
            {isCustomersLoading && <OptionRowsSkeleton rows={3} />}
            {customers.map((customer) => (
              <SelectItem key={customer.CustomerId} value={customer.CustomerId}>
                {customer.CustomerName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={status}
          onValueChange={(value) => setStatus(value as MbmProjectStatusFilter)}
        >
          <SelectTrigger className="w-full sm:w-44" aria-label={t('STATUS')}>
            <SelectValue placeholder={t('STATUS')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('MBM_PROJECT_ALL_STATUSES')}</SelectItem>
            <SelectItem value="active">{t('ACTIVE')}</SelectItem>
            <SelectItem value="inactive">{t('INACTIVE')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{ ...pagination, totalCount }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <MbmProjectFormDialog
        open={formState.open}
        onOpenChange={(open) =>
          setFormState((prev) => ({ open, project: open ? prev.project : null }))
        }
        project={formState.project}
      />

      <MbmProjectHistoryDialog
        open={historyState.open}
        onOpenChange={(open) =>
          setHistoryState((prev) => ({ open, project: open ? prev.project : null }))
        }
        project={historyState.project}
      />

      <ConfirmationModal
        open={statusState.open}
        onOpenChange={(open) =>
          setStatusState((prev) => ({ open, project: open ? prev.project : null }))
        }
        title={willActivate ? t('MBM_PROJECT_ACTIVATE') : t('MBM_PROJECT_DEACTIVATE')}
        description={
          willActivate ? t('MBM_PROJECT_ACTIVATE_CONFIRM') : t('MBM_PROJECT_DEACTIVATE_CONFIRM')
        }
        confirmText={willActivate ? t('ACTIVATE') : t('DEACTIVATE')}
        preventAutoClose={isUpdatingStatus}
        onConfirm={handleStatusConfirm}
      />
    </div>
  );
};
